import { Typography } from "@mui/material";
import type { BracketMatchResponse } from "@bw/shared/types/generated";
import { MatchCard, type MatchCardSide } from "../../../common/components/MatchCard";

/**
 * One server bracket match as the approved match card. Pure presentation over
 * the server's values: the score string ("21-18, 18-21") is split into game
 * columns, the winner comes from `winner_entry_id`, the court and time are the
 * server's. Nothing is derived beyond formatting.
 */

const timeFormat = new Intl.DateTimeFormat("en-US", {
  hour: "numeric",
  minute: "2-digit",
});

/** "21-18, 18-21" → [[21, 18], [18, 21]] per side; null when not game scores. */
export function parseGameScores(
  score: string | null | undefined,
): [number[], number[]] | null {
  if (!score) return null;
  const games = score.split(",").map((game) => game.trim().match(/^(\d+)\s*[-–]\s*(\d+)$/));
  if (games.length === 0 || games.some((game) => game === null)) return null;
  return [
    games.map((game) => Number(game![1])),
    games.map((game) => Number(game![2])),
  ];
}

export function isLiveMatch(match: BracketMatchResponse): boolean {
  return match.status === "in_progress";
}

/** "2" → "Court 2"; a named court ("Court 2", "Centre") is shown as named. */
export function courtLabel(name: string | null | undefined): string | null {
  if (!name) return null;
  return /^\d/.test(name.trim()) ? `Court ${name.trim()}` : name.trim();
}

function headerFor(
  match: BracketMatchResponse,
  bye: boolean,
  roundLabel?: string,
): string {
  const court = courtLabel(match.court_name);
  if (match.status === "in_progress") return court ?? "In play";
  if (match.status === "cancelled") return "Not played";
  if (match.status === "completed") {
    const label = bye ? "Bye" : match.score === "w/o" ? "Walkover" : court;
    return ["Final", roundLabel, label].filter(Boolean).join(" · ");
  }
  const time = match.scheduled_at ? timeFormat.format(new Date(match.scheduled_at)) : null;
  return [court, time ? `Next ~${time}` : "Upcoming"].filter(Boolean).join(" · ");
}

export function BracketMatchCard({
  match,
  mine = false,
  roundLabel,
}: {
  match: BracketMatchResponse;
  mine?: boolean;
  /** Short round name ("QF") shown on results. */
  roundLabel?: string;
}) {
  const scores = parseGameScores(match.score);
  const decided = match.winner_entry_id != null;
  const bye =
    match.status === "completed" && (match.entry1_id == null || match.entry2_id == null);
  const side = (index: 1 | 2): MatchCardSide => {
    const entryId = index === 1 ? match.entry1_id : match.entry2_id;
    const name = index === 1 ? match.entry1_display_name : match.entry2_display_name;
    return {
      // The bracket sends one entry name (a team as "A & B"); it is shown as
      // sent on one line — a user-editable name is never split.
      players: entryId == null ? [] : [{ name: name?.trim() || "—" }],
      placeholder: entryId == null ? (bye ? "Bye" : "TBD") : undefined,
      won: decided && entryId != null && match.winner_entry_id === entryId,
      scores: scores ? scores[index - 1] : undefined,
    };
  };
  return (
    <MatchCard
      header={headerFor(match, bye, roundLabel)}
      live={isLiveMatch(match)}
      mine={mine}
      ariaLabel={`Round ${match.round_number} · Match ${match.position}`}
      sides={[side(1), side(2)]}
      footer={
        match.score && !scores && match.score !== "w/o" ? (
          <Typography variant="body2" sx={{ pt: 0.5, fontWeight: 700 }}>
            {match.score}
          </Typography>
        ) : null
      }
    />
  );
}
